<template>
  <div class="locations-screen">
    <div class="row text-right mt-5 mr-20" v-if="loggedInUser.role === 'admin'">
      <base-button
        :title="$t('app.generals.createNew')"
        classNames="btn btn-orange rounded pull-right"
        @clicked="clickedCreateLocation"
      />
    </div>
    <configurable-list
      :loadingText="loadingText"
      v-if="model && model.locations"
      :data="model.locations"
      :fields="loggedInUser.role === 'admin' ? fields : partnedAdminFields"
      @nestedActionMade="actionMade"
      @openMap="openMap"
      @orderTable="orderTable"
    />
    <paginator-component
      :totalPages="totalPages"
      :currentPageIndex="currentPageIndex"
      @itemSelected="itemSelected"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ConfigurableList from '@/components/shared/lists/ConfigurableList';
import Paginator from '@/components/shared/navigations/Paginator';
import BaseButton from '@/components/shared/buttons/BaseButton';
import GenericForm from '@/components/shared/forms/GenericForm';

export default {
  name: 'LocationsScreen',
  components: {
    'configurable-list': ConfigurableList,
    'paginator-component': Paginator,
    'base-button': BaseButton,
  },
  props: {
    model: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      order: undefined,
      direction: undefined,
      selectedColor: '#ff0000',
      currentPageIndex: 1,
      newLocation: {
        site_id: undefined,
        name: undefined,
        description: undefined,
      },
      map_url: undefined,
      storedSiteId: undefined,
      storedId: undefined,
      area: [],
      colorList: [
        {
          value: '#ff000080',
          text: '#ff000080',
        },
        {
          value: '#00ff0080',
          text: '#00ff0080',
        },
        {
          value: '#0000ff80',
          text: '#0000ff80',
        },
        {
          value: '#ffd70080',
          text: '#ffd70080',
        },
        {
          value: '#80008080',
          text: '#80008080',
        },
        {
          value: '#ffffff80',
          text: '#ffffff80',
        },
        {
          value: '#00000080',
          text: '#00000080',
        },
        {
          value: '#00ffff80',
          text: '#00ffff80',
        },
        {
          value: '#ff00ff80',
          text: '#ff00ff80',
        },
        {
          value: '#06553580',
          text: '#06553580',
        },
        {
          value: '#ff737380',
          text: '#ff737380',
        },
        {
          value: '#ffff0080',
          text: '#ffff0080',
        },
        {
          value: '#ffc3a080',
          text: '#ffc3a080',
        },
        {
          value: '#20b2aa80',
          text: '#20b2aa80',
        },
        {
          value: '#f0808080',
          text: '#f0808080',
        },
        {
          value: '#f6546a80',
          text: '#f6546a80',
        },
        {
          value: '#66cdaa80',
          text: '#66cdaa80',
        },
        {
          value: '#66006680',
          text: '#66006680',
        },
        {
          value: '#8b000080',
          text: '#8b000080',
        },
        {
          value: '#79404480',
          text: '#79404480',
        },
      ],
    };
  },
  created() {
    this.fetchSite({
      partnerId: this.loggedInUser.partner_id,
      id: this.$route.params.id,
    }).then((response) => {
      this.map_url = response.map_url;
    });
  },
  computed: {
    ...mapGetters({
      commonData: 'partners/commonData',
      loggedInUser: 'loggedInUser',
      sites: 'sites/allSites',
      locations: 'locations/allLocations',
      storedLocation: 'locations/selectedLocation',
      locationSite: 'sites/selectedSite',
      partner: 'partners/selectedPartner',
    }),
    fields() {
      return [
        {
          title: this.$t('app.tables.locations.name'),
          cellClass: 'name-cell',
          key: 'name',
          isLocation: true,
          orderable: true,
        },
        {
          title: this.$t('app.tables.locations.description'),
          cellClass: 'description-cell',
          key: 'description',
          orderable: true,
        },
        {
          title: this.$t('app.tables.global.actions'),
          isAction: true,
          isNested: true,
          cellClass: 'edit-cell',
          key: 'edit',
          class: 'zmdi zmdi-edit',
        },
        {
          title: '',
          isAction: true,
          isNested: true,
          cellClass: 'delete-cell',
          key: 'delete',
          class: 'zmdi zmdi-delete',
        },
      ];
    },
    partnedAdminFields() {
      return [
        {
          title: this.$t('app.tables.locations.name'),
          cellClass: 'name-cell',
          key: 'name',
          isLocation: true,
          orderable: true,
        },
        {
          title: this.$t('app.tables.locations.description'),
          cellClass: 'description-cell',
          key: 'description',
          orderable: true,
        },
      ];
    },
    loadingText() {
      if (this.model && this.model.locations.length === 0) {
        return this.$t('app.tasks.empty_list');
      }
      return this.$t('app.listLoading');
    },
    totalPages() {
      return this.locations.total_pages || 0;
    },
    locationAttributes() {
      return [
        {
          name: 'area_color',
          default_key: 'web_area_color',
          type: 'dropdown',
          action: 'selectColor',
          dropdownList: this.colorList,
          classes: 'col-md-12',
        },
        {
          name: 'area',
          type: 'geometry',
          area: this.area,
          url: this.map_url,
          classes: 'col-md-12',
        },
        {
          name: 'name',
          type: 'text',
          classes: 'col-md-6',
        },
        {
          name: 'description',
          type: 'text',
          classes: 'col-md-6',
        },
      ];
    },
    locationMapAttributes() {
      return [
        {
          name: 'site_id',
          type: 'dropdown',
          action: 'selectSite',
          dropdownList: this.sitesForList,
          classes: 'col-md-12',
        },
        {
          name: 'area',
          type: 'geometry',
          area: this.area,
          url: this.map_url,
          classes: 'col-md-12',
        },
      ];
    },
    sitesForList() {
      return this.sites.sites.map(site => ({
        value: site.id,
        text: site.name,
      }));
    },
  },
  methods: {
    ...mapActions({
      fetchLocations: 'locations/fetchLocations',
      fetchLocation: 'locations/fetchLocation',
      createNewLocation: 'locations/createLocation',
      updateLocationParams: 'locations/updateLocation',
      deleteLocation: 'locations/deleteLocation',
      fetchSite: 'sites/fetchSite',
      fetchCommonData: 'partners/fetchCommonData',
    }),
    actionMade(payload) {
      if (payload.actionType === 'edit') {
        this.fetchSite({
          partnerId: this.loggedInUser.partner_id,
          id: payload.row.site_id,
        }).then((site) => {
          this.map_url = site.map_url;
          this.fetchLocation({
            partnerId: this.$route.params.id,
            siteId: payload.row.site_id,
            id: payload.row.id,
          })
            .then((location) => {
              this.storedSiteId = this.storedLocation.site_id;
              this.storedId = this.storedLocation.id;
              this.area = location.area;
              this.selectedColor = location.web_area_color;
              location.area_color = location.web_area_color;
              this.$eventBus.showModal({
                bind: {
                  is: GenericForm,
                  title: this.$t('app.locations.edit_title'),
                  fields: this.locationAttributes,
                  model: location,
                  submitTitle: this.$t('app.locations.buttons.save'),
                  cancelTitle: this.$t('app.locations.buttons.cancel'),
                  modelName: 'locations',
                  color: this.selectedColor,
                },
                on: {
                  sendAction: this.sendUpdateAction,
                  submit: this.updateLocation,
                },
              });
            })
            .catch((error) => {
              const mappedErrors = error.map(x => ` ${x}`);
              const errorMessages = this.$t('app.errors.general_validation', {
                message: mappedErrors,
              });
              this.$swal
                .fire({
                  html: errorMessages,
                  type: 'error',
                  showCloseButton: true,
                  showCancelButton: false,
                  showConfirmButton: false,
                  focusConfirm: false,
                });
            });
        });
      } else if (payload.actionType == 'delete') {
        this.$swal
          .fire({
            html: this.$t('app.notifications.general_confirm.title'),
            type: 'info',
            showCloseButton: true,
            showCancelButton: true,
            focusConfirm: false,
            confirmButtonText: this.$t('app.notifications.general_confirm.ok'),
            cancelButtonText: this.$t('app.notifications.general_confirm.cancel'),
          })
          .then((result) => {
            if (result.value) {
              const previousTotalPages = this.totalPages;
              this.deleteLocation({
                partnerId: this.partner.id,
                siteId: payload.row.site_id,
                id: payload.row.id,
              })
                .then(() => {
                  this.$eventBus.showNotification({
                    notificationText: this.$t('app.locations.success.delete'),
                    notificationType: 'success',
                  });
                  this.fetchLocations({
                    siteId: this.$route.params.id,
                    pageIndex: this.currentPageIndex,
                  }).then(() => {
                    if (this.totalPages < previousTotalPages) {
                      this.currentPageIndex = this.totalPages;
                    }
                  });
                })
                .catch((error) => {
                  this.$swal
                    .fire({
                      html: error[0],
                      type: 'error',
                      showCloseButton: true,
                      showCancelButton: false,
                      showConfirmButton: false,
                      focusConfirm: false,
                    });
                });
            }
          });
      }
    },
    updateLocation(location) {
      this.updateLocationParams({
        partnerId: this.$route.params.id,
        siteId: this.storedSiteId,
        id: location.id,
        params: location,
      })
        .then((response) => {
          this.$eventBus.showNotification({
            notificationText: this.$t('app.locations.success.update'),
            notificationType: 'success',
          });
          this.fetchLocations({
            siteId: this.$route.params.id,
            pageIndex: this.currentPageIndex,
          })
            .then((response) => {
              this.$eventBus.hideModal();
            })
            .catch((error) => {
              this.$swal
                .fire({
                  html: error[0],
                  type: 'error',
                  showCloseButton: true,
                  showCancelButton: false,
                  showConfirmButton: false,
                  focusConfirm: false,
                });
            });
        })
        .catch((error) => {
          this.$eventBus.stopLoading();
          const mappedErrors = error.map(x => ` ${x}`);
          this.$eventBus.getError(mappedErrors);
        });
    },
    clickedCreateLocation() {
      if (this.partner.site) {
        this.$eventBus.showModal({
          bind: {
            is: GenericForm,
            title: this.$t('app.locations.set_title'),
            fields: this.locationAttributes,
            model: this.newLocation,
            submitTitle: this.$t('app.locations.buttons.save'),
            cancelTitle: this.$t('app.locations.buttons.cancel'),
            modelName: 'locations',
            color: this.selectedColor,
          },
          on: {
            sendAction: this.sendAction,
            submit: this.createLocation,
            modalCanceled: this.resetNewLocation,
          },
        });
      } else {
        this.$swal
          .fire({
            html: this.$t('app.locations.need_site'),
            type: 'error',
            showCloseButton: true,
            showCancelButton: false,
            showConfirmButton: false,
            focusConfirm: false,
          });
      }
    },
    createLocation(newLocation) {
      newLocation.site_id = this.$route.params.id;
      this.createNewLocation({
        site_id: this.$route.params.id,
        params: newLocation,
      })
        .then(() => {
          this.$eventBus.showNotification({
            notificationText: this.$t('app.locations.success.created'),
            notificationType: 'success',
          });
          this.fetchLocations({
            siteId: this.$route.params.id,
            pageIndex: this.currentPageIndex,
          })
            .then(() => {
              this.resetNewLocation();
              this.$eventBus.hideModal();
            })
            .catch((error) => {
              this.$swal
                .fire({
                  html: error[0],
                  type: 'error',
                  showCloseButton: true,
                  showCancelButton: false,
                  showConfirmButton: false,
                  focusConfirm: false,
                });
            });
        })
        .catch((error) => {
          this.$eventBus.stopLoading();
          const mappedErrors = error.map(x => ` ${x}`);
          this.$eventBus.getError(mappedErrors);
        });
    },
    resetNewLocation() {
      Object.assign(this.$data.newLocation, this.$options.data().newLocation);
    },
    itemSelected(index) {
      this.currentPageIndex = index;
    },
    sendAction(payload) {
      if (payload.action === 'selectSite') {
        this.fetchSite({
          partnerId: this.loggedInUser.partner_id,
          id: payload.value,
        }).then((site) => {
          this.map_url = site.map_url;
          this.clickedCreateLocation();
        });
      } else if (payload.action === 'selectColor') {
        this.selectedColor = payload.value;
        this.clickedCreateLocation();
      }
    },
    sendUpdateAction(payload) {
      if (payload.action === 'selectSite') {
        this.fetchSite({
          partnerId: this.loggedInUser.partner_id,
          id: payload.value,
        })
          .then((site) => {
            this.map_url = site.map_url;
            this.storedId = this.storedLocation.id;
            this.$eventBus.showModal({
              bind: {
                is: GenericForm,
                title: this.$t('app.locations.edit_title'),
                fields: this.locationAttributes,
                model: this.storedLocation,
                submitTitle: this.$t('app.locations.buttons.save'),
                cancelTitle: this.$t('app.locations.buttons.cancel'),
                modelName: 'locations',
                color: this.selectedColor,
              },
              on: {
                sendAction: this.sendUpdateAction,
                submit: this.updateLocation,
              },
            });
          })
          .catch((error) => {
            this.$swal
              .fire({
                html: error[0],
                type: 'error',
                showCloseButton: true,
                showCancelButton: false,
                showConfirmButton: false,
                focusConfirm: false,
              });
          });
      } else if (payload.action === 'selectColor') {
        this.selectedColor = payload.value;
        this.$eventBus.showModal({
          bind: {
            is: GenericForm,
            title: this.$t('app.locations.edit_title'),
            fields: this.locationAttributes,
            model: this.storedLocation,
            submitTitle: this.$t('app.locations.buttons.save'),
            cancelTitle: this.$t('app.locations.buttons.cancel'),
            modelName: 'locations',
            color: this.selectedColor,
          },
          on: {
            sendAction: this.sendUpdateAction,
            submit: this.updateLocation,
          },
        });
      }
    },
    openMap(payload) {
      this.fetchSite({
        partnerId: this.loggedInUser.partner_id,
        id: payload.site_id,
      }).then((site) => {
        this.map_url = site.map_url;
        this.fetchLocation({
          partnerId: this.$route.params.id,
          siteId: payload.site_id,
          id: payload.id,
        })
          .then((location) => {
            this.storedSiteId = this.storedLocation.site_id;
            this.storedId = this.storedLocation.id;
            this.area = location.area;
            this.selectedColor = location.web_area_color;
            this.$eventBus.showModal({
              bind: {
                is: GenericForm,
                title: this.$t('app.locations.set_title'),
                fields: this.locationMapAttributes,
                model: location,
                submitTitle: this.$t('app.locations.buttons.save'),
                cancelTitle: this.$t('app.locations.buttons.cancel'),
                modelName: 'locations',
                color: this.selectedColor,
                readOnly: true,
              },
              on: {
                sendAction: this.sendUpdateAction,
                submit: this.updateLocation,
              },
              width: '1000px',
            });
          })
          .catch((error) => {
            this.$swal
              .fire({
                html: error[0],
                type: 'error',
                showCloseButton: true,
                showCancelButton: false,
                showConfirmButton: false,
                focusConfirm: false,
              });
          });
      });
    },
    orderTable(payload) {
      this.order = payload.order;
      this.direction = payload.direction;

      this.fetchLocations({
        siteId: this.$route.params.id,
        pageIndex: this.currentPageIndex,
        orderInfo: {
          order: payload.order,
          direction: payload.direction,
        },
      });
    },
  },
  watch: {
    currentPageIndex(newValue, oldValue) {
      if (this.order !== undefined && this.direction !== undefined) {
        this.fetchLocations({
          siteId: this.$route.params.id,
          pageIndex: newValue,
          orderInfo: {
            order: this.order,
            direction: this.direction,
          },
        });
      } else {
        this.fetchLocations({
          siteId: this.$route.params.id,
          pageIndex: newValue,
        });
      }
    },
  },
};
</script>

<style lang="scss">
.locations-screen {
   /deep/ .configurable-list {
    .card-view {
      .panel-heading {
        .row {
          margin-right: 50%;
        }
        margin-top: -70px;
      }
    }
  }
  .rounded {
    border-radius: 4px;
  }
  .id-cell {
    width: 10%;
  }

  .site-cell {
    width: 10%;
  }

  .name-cell {
    width: 20%;
  }

  .address-cell {
    width: 25%;
  }

  .description-cell {
    width: 25%;
  }

  .edit-cell {
    width: 2.5%;
  }

  .delete-cell {
    width: 2.5%;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/modules/partners/LocationsScreen.vue